import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import Icon from './icon';
import tw from '../helpers/tw';
import ExtractSheet from './extract-sheet';
import HomeIcon from '~icons/lucide/home';
import MapIcon from '~icons/lucide/map';
import CompassIcon from '~icons/lucide/compass';
import UserIcon from '~icons/lucide/user';
import PlusIcon from '~icons/lucide/plus';

interface NavLink {
  route: string;
  label: string;
  icon: typeof HomeIcon;
}

const LEFT_LINKS: NavLink[] = [
  { route: 'index', label: 'Home', icon: HomeIcon },
  { route: 'places', label: 'Places', icon: MapIcon },
];

const RIGHT_LINKS: NavLink[] = [
  { route: 'explore', label: 'Explore', icon: CompassIcon },
  { route: 'my-account', label: 'Account', icon: UserIcon },
];

interface MobileDockSignature {
  Args: {};
  Element: HTMLDivElement;
}

export default class MobileDock extends Component<MobileDockSignature> {
  @tracked isHidden = false;
  @tracked isExtractOpen = false;

  private lastScrollY = 0;
  private readonly handleScroll: () => void;

  constructor(owner: Owner, args: MobileDockSignature['Args']) {
    super(owner, args);

    this.lastScrollY = window.scrollY;

    this.handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - this.lastScrollY;

      if (delta > 10 && !this.isHidden) {
        this.isHidden = true;
      } else if (delta < -5 && this.isHidden) {
        this.isHidden = false;
      }

      this.lastScrollY = currentY;
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    registerDestructor(this, () => {
      window.removeEventListener('scroll', this.handleScroll);
    });
  }

  @action
  openExtract() {
    this.isExtractOpen = true;
  }

  @action
  closeExtract() {
    this.isExtractOpen = false;
  }

  <template>
    <div class="dock-container pb-[env(safe-area-inset-bottom)] lg:hidden">
      <div
        inert={{if this.isHidden true}}
        class={{tw
          "dock dock-sm bg-base-200/80 backdrop-blur-sm transition-transform duration-300"
          (if this.isHidden "translate-y-full")
        }}
      >
        {{#each LEFT_LINKS as |link|}}
          <LinkTo
            @route={{link.route}}
            @activeClass="dock-active"
            class="transition-transform duration-150 active:scale-90"
          >
            <Icon @icon={{link.icon}} class="h-5 w-5" />
            <span class="dock-label">{{link.label}}</span>
          </LinkTo>
        {{/each}}

        {{! Central Extract button }}
        <button
          type="button"
          class="dock-item transition-transform duration-150 active:scale-90"
          aria-label="Extract content"
          {{on "click" this.openExtract}}
        >
          <Icon @icon={{PlusIcon}} class="h-5 w-5" />
          <span class="dock-label">Extract</span>
        </button>

        {{#each RIGHT_LINKS as |link|}}
          <LinkTo
            @route={{link.route}}
            @activeClass="dock-active"
            class="transition-transform duration-150 active:scale-90"
          >
            <Icon @icon={{link.icon}} class="h-5 w-5" />
            <span class="dock-label">{{link.label}}</span>
          </LinkTo>
        {{/each}}
      </div>
    </div>

    <ExtractSheet
      @isOpen={{this.isExtractOpen}}
      @onClose={{this.closeExtract}}
    />
  </template>
}
